import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Eye,
  Trash2
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminMessages } from '../../hooks/useAdminMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminMessages = () => {
  const navigate = useNavigate();
  const {
    messages,
    isLoading,
    error,
    filters,
    readStatusOptions,
    messageTypeOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    deleteMessage
  } = useAdminMessages();

  // Define table columns
  const columns = [
    {
      key: 'id',
      label: 'Message ID',
      sortable: true,
      width: 'w-32',
      render: (message) => (
        <span className="font-mono text-sm font-medium text-primary-600">
          #{message.id.substring(0, 8)}
        </span>
      )
    },
    {
      key: 'sender',
      label: 'From',
      sortable: true,
      width: 'w-48',
      render: (message) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {message.sender?.name || 'System'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {message.sender?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'receiver',
      label: 'To',
      sortable: true,
      width: 'w-48',
      render: (message) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {message.receiver?.name || 'N/A'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {message.receiver?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      width: 'w-64',
      render: (message) => (
        <p className="text-sm text-gray-900 truncate">
          {message.message || 'N/A'}
        </p>
      )
    },
    {
      key: 'booking',
      label: 'Booking',
      sortable: true,
      width: 'w-32',
      render: (message) => (
        message.booking ? (
          <button
            onClick={() => navigate(`/admin/bookings/${message.booking_id}`)}
            className="font-mono text-sm font-medium text-primary-600 hover:underline"
          >
            #{message.booking.booking_reference}
          </button>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (message) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(message.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(message.created_at)}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_read',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (message) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          message.is_read 
            ? 'bg-green-100 text-green-800' 
            : 'bg-yellow-100 text-yellow-800'
        }`}>
          {message.is_read ? 'Read' : 'Unread'}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: (message) => {
        // Could open a modal or navigate to message details
        console.log('View message:', message);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (message) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
          deleteMessage(message.id);
        }
      }
    }
  ];

  const { totalMessages, unreadMessages, readMessages } = statistics;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Messages</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load messages. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-6 h-6" />
                Messages
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage and monitor all platform messages
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalMessages}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{unreadMessages}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Read</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{readMessages}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Read Status"
              value={filters.is_read}
              onChange={(value) => handleFilterChange('is_read', value)}
              options={readStatusOptions}
            />
            <SearchSelect
              label="Message Type"
              value={filters.message_type}
              onChange={(value) => handleFilterChange('message_type', value)}
              options={messageTypeOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={messages}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search messages by content, sender, receiver, or booking..."
            onSearch={handleSearch}
            emptyMessage="No messages found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;

